import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Check, MessageCircle, Gift, Video, Sparkles, Phone } from "lucide-react";
import { apiClient } from "../../lib/api";
import {
  trackFbEvent,
  readFbCookies,
  generateEventId,
  trackThenNavigate,
  getFbclidFromUrl,
  getOrCreateExternalId,
} from "../../lib/fbpixel";
import { fireViewContent } from "../../lib/capiClient";
import { hasAdConsent } from "../../lib/consent";
import { COUNTRY_PREFIXES, toDialCode } from "../../lib/countryCodes";
import { suggestEmailFix } from "../../lib/emailTypo";
import { attributionPayload } from "../../lib/attribution";
import {
  isLeadInFlight,
  isLeadSent,
  beginLead,
  endLead,
  clearLead,
  readChannelUrl,
} from "../../lib/leadGuard";
import SocialProofBlock from "./SocialProofBlock";

const VIEW_CONTENT_SESSION_KEY = "sircs_viewcontent_fired";

/**
 * Split "María García López" into { first: "María", last: "García López" }.
 * If only one word, last is null (so Meta CAPI omits `ln`).
 */
function splitName(full) {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: null };
  if (parts.length === 1) return { first: parts[0], last: null };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

/**
 * Reusable reservation form. Rendered both in the hero (above the fold) and
 * inside the #reservar section further down the page. The `variant` prop
 * lets each placement lightly tweak spacing/typography without duplicating
 * logic. The Lead event is deduplicated across instances via `leadGuard.js`.
 *
 * Props:
 *   - variant: "hero" | "section" (default "section")
 *   - observeViewContent: bool — if true, this instance triggers the
 *     ViewContent event once per session when it enters the viewport.
 */
export default function MasterclassForm({
  variant = "section",
  observeViewContent = false,
}) {
  const [form, setForm] = useState({
    full_name: "",
    prefix: "+34",
    phone: "",
    email: "",
  });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(() => {
    // If the user already reserved in this session, show the success card
    // straight away instead of an empty form. Cross-tab safe via sessionStorage.
    if (typeof window !== "undefined" && isLeadSent()) {
      const url = readChannelUrl();
      return { whatsapp_url: url || "", _restored: true };
    }
    return null;
  });
  const [emailTypoSuggestion, setEmailTypoSuggestion] = useState(null);
  const eventIdRef = useRef(null);
  const rootRef = useRef(null);

  // ViewContent observer — ONLY the instance flagged with observeViewContent
  // (the hero one) sets it up. Robust: measures viewport-fraction, not target
  // ratio. Includes fast path and scroll safety net.
  useEffect(() => {
    if (!observeViewContent) return;
    if (typeof window === "undefined") return;
    const target = rootRef.current;
    if (!target) return;
    let alreadyFired = false;
    try {
      alreadyFired = !!window.sessionStorage.getItem(VIEW_CONTENT_SESSION_KEY);
    } catch (_) {}
    if (alreadyFired) return;

    const fireOnce = () => {
      if (alreadyFired) return;
      alreadyFired = true;
      fireViewContent();
      try {
        window.sessionStorage.setItem(VIEW_CONTENT_SESSION_KEY, "1");
      } catch (_) {}
    };

    const isSeen = () => {
      const rect = target.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (vh <= 0) return false;
      const visible = Math.max(0, Math.min(vh, rect.bottom) - Math.max(0, rect.top));
      return visible / vh >= 0.35;
    };

    if (isSeen()) {
      fireOnce();
      return;
    }

    let io = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && isSeen()) {
              fireOnce();
              if (io) io.disconnect();
              io = null;
              break;
            }
          }
        },
        {
          threshold: [0, 0.05, 0.15, 0.25, 0.5],
          rootMargin: "0px 0px -20% 0px",
        }
      );
      io.observe(target);
    }
    const onScroll = () => {
      if (alreadyFired) {
        window.removeEventListener("scroll", onScroll);
        return;
      }
      if (isSeen()) {
        fireOnce();
        window.removeEventListener("scroll", onScroll);
        if (io) io.disconnect();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (io) io.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [observeViewContent]);

  const submit = async (e) => {
    e.preventDefault();
    if (isLeadInFlight() || isLeadSent()) {
      toast.error("Ya estás en la lista de espera.");
      return;
    }
    const { first: first_name, last: last_name } = splitName(form.full_name);
    if (!first_name) {
      toast.error("Escribe tu nombre");
      return;
    }
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 6) {
      toast.error("Número de teléfono inválido");
      return;
    }
    const emailRaw = form.email.trim();
    if (emailRaw && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRaw)) {
      toast.error("El email no es válido");
      return;
    }
    if (!agree) {
      toast.error("Debes leer y aceptar la Política de Privacidad");
      return;
    }
    beginLead();
    setLoading(true);

    const eventId = generateEventId();
    eventIdRef.current = eventId;
    const { fbp, fbc } = readFbCookies();
    const fbclid = getFbclidFromUrl();
    const externalId = getOrCreateExternalId();
    const selected = COUNTRY_PREFIXES.find((c) => c.code === form.prefix);
    const dial = toDialCode(form.prefix);

    const payload = {
      first_name,
      last_name: last_name || null,
      phone_country_code: dial,
      phone_number: digits,
      country: selected?.country || "",
      email: emailRaw || null,
      event_id: eventId,
      fbp,
      fbc,
      fbclid,
      external_id: externalId,
      event_source_url: typeof window !== "undefined" ? window.location.href : "",
      // AEPD: tell the backend whether the user granted advertising cookies.
      // The backend uses this to decide if it can fire the Meta CAPI Lead.
      ad_consent: hasAdConsent(),
      ...attributionPayload(),
    };

    let saved = false;
    let response = null;
    try {
      response = await apiClient.post("/masterclass/register", payload);
      // Only consider the reservation confirmed once the backend has
      // acknowledged with 2xx and returned an id. axios rejects on 4xx/5xx,
      // so reaching this line means the row is in Mongo.
      if (response?.data?.id) {
        saved = true;
      }
    } catch (err) {
      // Network drop, timeout, 4xx or 5xx: leave the guard OPEN so the user
      // can retry immediately with the same data (no reload needed).
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg = "No se pudo enviar. Comprueba tu conexión y vuelve a pulsar el botón.";
      if (detail) msg = detail;
      else if (status && status >= 500) msg = "Error del servidor. Vuelve a intentarlo en unos segundos.";
      else if (err?.code === "ECONNABORTED") msg = "La red tardó demasiado. Vuelve a intentarlo.";
      toast.error(msg);
    } finally {
      // Always release the in-flight lock. Only persist "sent" on saved=true.
      endLead({
        success: saved,
        channelUrl: saved ? response?.data?.whatsapp_url : null,
      });
      setLoading(false);
    }
    if (!saved) return; // do NOT fire pixel or navigate on failure

    setDone(response.data);
    trackThenNavigate(
      () => {
        // AEPD: only fire browser Pixel Lead/Contact when the user opted in
        // for advertising cookies. Server-side CAPI is gated by ad_consent
        // in the payload (see /api/masterclass/register).
        if (hasAdConsent()) {
          trackFbEvent(
            "Lead",
            {
              content_name: "Masterclass en directo — Tu segundo acto",
              content_category: "masterclass_registration",
            },
            eventId
          );
          trackFbEvent(
            "Contact",
            { content_name: "WhatsApp Canal (post-reserva)" },
            eventId
          );
        }
      },
      () => {
        if (!response.data?.whatsapp_url) return;
        try {
          window.location.href = response.data.whatsapp_url;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[reservar] navigation failed:", err);
        }
      },
      300
    );

    setTimeout(() => {
      const el = rootRef.current?.querySelector('[data-testid="masterclass-success"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    toast.success("¡Listo! Abriendo el canal…");
  };

  /** Reset the guard so the user can reserve another plaza (couples, wrong number). */
  const reserveAgain = () => {
    clearLead();
    setDone(null);
    setForm({ full_name: "", prefix: "+34", phone: "", email: "" });
    setAgree(false);
    setEmailTypoSuggestion(null);
  };

  const openChannelFromSuccess = (e) => {
    if (!done?.whatsapp_url) return;
    e.preventDefault();
    const eventId = eventIdRef.current || generateEventId();
    trackThenNavigate(
      () => {
        trackFbEvent(
          "Contact",
          { content_name: "WhatsApp Canal — clic directo en éxito" },
          eventId
        );
      },
      () => {
        window.location.href = done.whatsapp_url;
      },
      300
    );
  };

  const containerCls =
    variant === "hero"
      ? "paper p-6 md:p-7 relative"
      : "paper p-8 md:p-10 relative";

  return (
    <div ref={rootRef} className={containerCls} data-testid={`masterclass-form-${variant}`}>
      {!done ? (
        <>
          <h3 className="font-serif font-normal text-2xl md:text-3xl text-[#0B0E13] mb-2 tracking-[-0.005em]">
            Entra en la lista de espera
          </h3>
          <p className="text-base text-[#6E7580] mb-7 font-sans font-light">
            Al enviar entrarás en el canal privado de WhatsApp. Ahí anuncio la fecha de la
            próxima masterclass y te llega el enlace de acceso.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="masterclass-form">
            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#6E7580] font-medium font-sans">
                Nombre
              </label>
              <input
                className="input-line mt-2"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="María"
                autoComplete="name"
                required
                data-testid="masterclass-full-name"
              />
            </div>

            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#6E7580] font-medium font-sans">
                Teléfono con WhatsApp
              </label>
              <div className="mt-2 flex items-stretch gap-2 border-b border-[#0B0E13]/20 focus-within:border-[#0B0E13] transition-colors">
                <select
                  value={form.prefix}
                  onChange={(e) => setForm({ ...form, prefix: e.target.value })}
                  className="bg-transparent py-[15px] pr-2 text-[#0B0E13] font-sans font-medium text-[15px] focus:outline-none cursor-pointer max-w-[140px]"
                  required
                  data-testid="masterclass-prefix"
                  aria-label="Prefijo país"
                >
                  {COUNTRY_PREFIXES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {toDialCode(c.code)} — {c.country}
                    </option>
                  ))}
                </select>
                <span className="self-center h-6 w-px bg-[#0B0E13]/15" aria-hidden="true" />
                <input
                  type="tel"
                  inputMode="tel"
                  className="flex-1 bg-transparent border-0 py-[15px] text-[#0B0E13] font-sans font-normal text-[16px] placeholder:text-[#0B0E13]/40 focus:outline-none"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: e.target.value.replace(/[^\d\s]/g, "") })
                  }
                  placeholder="600 123 456"
                  autoComplete="tel-national"
                  required
                  data-testid="masterclass-phone"
                />
              </div>
              <p className="mt-2 text-[11px] text-[#6E7580] font-sans font-light flex items-start gap-1.5 leading-snug">
                <Phone size={11} strokeWidth={2} className="shrink-0 mt-0.5" />
                <span>
                  Te escribimos solo para darte el enlace y el recordatorio. Puedes salir del
                  canal cuando quieras.
                </span>
              </p>
            </div>

            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#6E7580] font-medium font-sans">
                Email (opcional - para enviarte el recordatorio y la grabación)
              </label>
              <input
                type="email"
                inputMode="email"
                className="input-line mt-2"
                value={form.email}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, email: v });
                  setEmailTypoSuggestion(suggestEmailFix(v));
                }}
                placeholder="tu@email.com"
                autoComplete="email"
                data-testid="masterclass-email"
              />
              {emailTypoSuggestion && (
                <button
                  type="button"
                  onClick={() => {
                    setForm({ ...form, email: emailTypoSuggestion });
                    setEmailTypoSuggestion(null);
                  }}
                  className="mt-2 text-[12px] text-left text-[#8E7A55] hover:text-[#0B0E13] transition-colors font-sans"
                  data-testid="email-typo-suggestion"
                >
                  ¿Querías decir <span className="underline underline-offset-2 font-medium">{emailTypoSuggestion}</span>?
                </button>
              )}
            </div>

            <label className="flex items-start gap-3 pt-1 cursor-pointer group">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-[#0B0E13]/30 accent-[#C7AA78]"
                data-testid="masterclass-agree"
              />
              <span className="text-[12px] text-[#6E7580] font-sans font-light leading-relaxed">
                He leído y acepto la{" "}
                <a
                  href="/politica-de-privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 text-[#0B0E13]"
                  data-testid="masterclass-privacy-link"
                >
                  Política de Privacidad
                </a>
                .
              </span>
            </label>

            <p className="text-[11px] text-[#6E7580] font-sans font-light leading-[1.55]">
              Responsable: Santiago Lorenzo Garrido. Finalidad: responder a tu solicitud y, si
              lo aceptas, enviarte comunicaciones. Derechos: acceso, rectificación, supresión
              y otros, en{" "}
              <a
                href="mailto:santilorenzogar@gmail.com"
                className="underline underline-offset-2 text-[#0B0E13]"
              >
                santilorenzogar@gmail.com
              </a>
              .
            </p>

            <div className="flex items-start gap-3 rounded-md border border-[#C7AA78]/45 bg-[#C7AA78]/8 px-4 py-3">
              <MessageCircle className="text-[#8E7A55] mt-0.5 shrink-0" size={16} strokeWidth={2} />
              <p className="text-[12px] leading-[1.55] text-[#3A414C] font-sans font-light">
                Al enviar entrarás al{" "}
                <span className="font-medium text-[#0B0E13]">canal privado de WhatsApp</span>.
                Ahí anuncio la fecha de la próxima masterclass y te llega el enlace de acceso.
              </p>
            </div>

            <button
              type="submit"
              className="btn-gold w-full mt-2 justify-center"
              disabled={loading}
              data-testid="masterclass-submit"
            >
              {loading ? "Enviando…" : "Avísame de la próxima →"}
            </button>

            <div className="pt-2">
              <SocialProofBlock variant="compact" />
            </div>
          </form>
        </>
      ) : (
        <div className="py-2" data-testid="masterclass-success">
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#0B0E13]/5 border border-[#0B0E13] mb-5">
              <Check className="text-[#0B0E13]" size={26} strokeWidth={2.5} />
            </div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#8E7A55] font-medium font-sans mb-2">
              En la lista de espera
            </p>
            <h3 className="font-serif font-normal text-3xl sm:text-4xl text-[#0B0E13] mb-3 tracking-[-0.005em] leading-tight">
              {done._restored ? (
                <>Ya estás <span className="italic text-[#6E7580]">apuntada/o</span>.</>
              ) : (
                <>
                  Un último paso,
                  <br />
                  <span className="italic text-[#6E7580]">
                    {form.full_name?.trim().split(/\s+/)[0] || "bienvenida/o"}
                  </span>
                  .
                </>
              )}
            </h3>
            <p className="text-[#2A303A] text-base leading-[1.65] max-w-md mx-auto font-sans font-light">
              La fecha de la próxima masterclass se anuncia
              <span className="font-serif italic text-[#0B0E13]"> dentro del canal privado de WhatsApp</span>,
              junto con contenido semanal exclusivo de Santi.
            </p>
          </div>

          {done.whatsapp_url && (
            <div className="mt-8 relative" data-testid="whatsapp-invite-block">
              <span
                className="absolute inset-0 -m-1 rounded-md pointer-events-none"
                style={{
                  boxShadow: "0 0 0 3px rgba(199,170,120,0.35)",
                  animation: "haloPulse 2.2s ease-in-out infinite",
                }}
                aria-hidden="true"
              />
              <a
                href={done.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={openChannelFromSuccess}
                className="relative flex items-center justify-center gap-3 w-full py-4 px-6 rounded-md font-medium text-[#0B0E13] text-sm sm:text-base tracking-[0.10em] uppercase transition-all duration-300"
                style={{
                  background: "#C7AA78",
                  boxShadow: "0 10px 30px -8px rgba(199,170,120,0.55)",
                }}
                data-testid="masterclass-whatsapp-btn"
              >
                <MessageCircle size={20} strokeWidth={2} />
                Entrar al canal ahora
                <span className="text-lg leading-none">→</span>
              </a>
              <p className="text-center text-[11px] text-[#6E7580] mt-3 font-sans font-light">
                Si tu navegador no lo abrió automáticamente, pulsa aquí.
              </p>
            </div>
          )}

          <div className="mt-8 rounded-md border border-[#0B0E13]/10 bg-[#FBF7F0] p-6">
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#8E7A55] font-medium font-sans mb-5">
              Qué recibirás dentro
            </p>
            <ul className="space-y-4">
              {[
                { Icon: Video, title: "El aviso de la próxima fecha", text: "Se publica en el canal en cuanto Santi confirme el próximo directo." },
                { Icon: Sparkles, title: "Contenido exclusivo semanal", text: "Reflexiones, audios y ejercicios directos de Santi." },
                { Icon: Gift, title: "Sorpresas para la comunidad", text: "Materiales, invitaciones y bonus solo para miembros." },
              ].map(({ Icon, title, text }) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="h-9 w-9 shrink-0 rounded-md border border-[#0B0E13]/12 bg-white flex items-center justify-center">
                    <Icon className="text-[#8E7A55]" size={17} strokeWidth={1.75} />
                  </span>
                  <div className="flex-1">
                    <p className="font-serif text-[#0B0E13] text-lg leading-tight tracking-[-0.005em]">{title}</p>
                    <p className="text-[#3A414C] text-sm leading-snug font-sans font-light mt-1">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Discreet "apuntar otro contacto" — for couples signing up from the
              same phone or for someone who typed the wrong number. */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={reserveAgain}
              className="text-[12px] text-[#6E7580] hover:text-[#0B0E13] underline underline-offset-4 font-sans transition-colors"
              data-testid="reserve-again-link"
            >
              Apuntar otro contacto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
